import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { flexRender, type RowData } from '@tanstack/react-table';
import {
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Loader2,
} from 'lucide-react';

import type { TableContentProps } from '../types';

import { IconButton } from '@/components';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { cn } from '@/lib/utils';

import { DraggableRow } from './DraggableRow';
import { EmptyTable } from './EmptyTable';
import { TableDndContext } from './TableDndContext';

export function TableContent<TData extends RowData>(
  props: TableContentProps<TData>,
) {
  const {
    table,
    columns,
    pending = false,
    loadingMessage,
    onRowClick,
    getRowClassName,
    renderSubRow,
    dnd,
    emptyState,
  } = props;

  const { t } = useTranslation();
  const hasExpandableRows = !!renderSubRow;
  const isDndEnabled = !!dnd;
  const leadingCols = (hasExpandableRows ? 1 : 0) + (isDndEnabled ? 1 : 0);

  const rows = table.getRowModel().rows;
  const isAllRowsExpanded = table.getIsAllRowsExpanded();
  const expandAllLabel = isAllRowsExpanded
    ? t('Collapse all')
    : t('Expand all');
  const rowIds = rows.map((row) => row.id);

  const renderedRows = rows.map((row) => {
    const cells = row.getVisibleCells().map((cell) => (
      <TableCell
        key={cell.id}
        className={cell.column.columnDef.meta?.className}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    ));

    if (isDndEnabled) {
      return (
        <DraggableRow
          key={row.id}
          id={row.id}
          className={getRowClassName?.(row.original, row.index) || ''}
        >
          {cells}
        </DraggableRow>
      );
    }

    return (
      <Fragment key={row.id}>
        <TableRow
          data-state={row.getIsSelected() && 'selected'}
          className={cn(
            getRowClassName?.(row.original, row.index),
            onRowClick && !hasExpandableRows && 'cursor-pointer',
            hasExpandableRows && 'border-b-0',
          )}
          onClick={() => !hasExpandableRows && onRowClick?.(row.original)}
        >
          {hasExpandableRows && (
            <TableCell>
              <Button
                variant="ghost"
                onClick={() => row.toggleExpanded()}
                className="size-8 p-0"
              >
                <ChevronRight
                  className={cn(
                    'size-4 transition-transform duration-200',
                    row.getIsExpanded() && 'rotate-90',
                  )}
                />
              </Button>
            </TableCell>
          )}
          {cells}
        </TableRow>
        {hasExpandableRows && (
          <TableRow>
            <TableCell
              colSpan={columns.length + 1}
              className="h-auto p-0"
            >
              <Collapsible open={row.getIsExpanded()}>
                <CollapsibleContent className="data-[state=closed]:animate-collapsible-up overflow-hidden">
                  <div className="grid grid-rows-[1fr] transition-[grid-template-rows] duration-200 starting:grid-rows-[0fr]">
                    <div className="min-h-0 overflow-hidden">
                      <div className="bg-muted/50 p-4">
                        {renderSubRow(row.original)}
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  });

  const tableElement = (
    <div className={cn(isDndEnabled && 'overflow-x-auto overflow-y-hidden')}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {isDndEnabled && <TableHead className="w-12" />}
              {hasExpandableRows && (
                <TableHead className="w-12">
                  {rows.length > 0 && (
                    <IconButton
                      label={expandAllLabel}
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => table.toggleAllRowsExpanded()}
                    >
                      {isAllRowsExpanded ? (
                        <ChevronsDownUp />
                      ) : (
                        <ChevronsUpDown />
                      )}
                    </IconButton>
                  )}
                </TableHead>
              )}
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={cn(
                    'whitespace-normal',
                    header.column.columnDef.meta?.className,
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {pending ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + leadingCols}
                className="bg-popover h-45 text-center"
              >
                <div className="flex flex-col items-center justify-center gap-2">
                  <Loader2 className="text-muted-foreground size-5 animate-spin" />
                  <span className="text-base font-semibold">
                    {loadingMessage || t('Loading...')}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          ) : rows.length ? (
            renderedRows
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length + leadingCols}
                className="p-0"
              >
                <EmptyTable state={emptyState} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );

  if (isDndEnabled && dnd) {
    return (
      <TableDndContext
        rowIds={rowIds}
        onReorder={dnd.onReorder}
      >
        {tableElement}
      </TableDndContext>
    );
  }

  return tableElement;
}
